import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Sparkles,
  Send,
  Plus,
  Trash2,
  MessageSquare,
  Loader2,
  ChevronLeft,
  Lock,
  User
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import MarkdownMessage from '../components/MarkdownMessage';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const ROOTY_IMG = 'https://customer-assets.emergentagent.com/job_5adcc4e8-1bb2-4cdf-8c44-5c6c1ccd2118/artifacts/1skt1b81_IMG_1754.png';

const QUICK_PROMPTS = [
  "Which store had the best performance this week?",
  "What are the top 3 areas we need to improve?",
  "Summarize our current KPI status across all stores",
  "How can we reduce food cost below 32%?",
  "What should I focus on during store visits this week?",
  "Create an action plan for improving drive-thru times"
];

export default function AIAssistant() {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [model, setModel] = useState('quick');
  const [pinnedModel, setPinnedModel] = useState(null);
  const [modelOptions, setModelOptions] = useState([]);
  const [showSidebar, setShowSidebar] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => { fetchSessions(); fetchModels(); }, []);
  useEffect(() => { if (activeSession) fetchMessages(activeSession); }, [activeSession]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // A conversation stays on the tier it started with — sync the picker to the session's pinned model.
  useEffect(() => {
    const s = sessions.find(x => x.id === activeSession);
    const hasHistory = s && ((s.message_count || 0) > 0 || messages.some(m => !String(m.id).startsWith('temp-')));
    if (s && hasHistory && s.model) {
      setPinnedModel(s.model);
      setModel(s.model);
    } else {
      setPinnedModel(null);
    }
  }, [activeSession, sessions, messages]);

  const fetchModels = async () => {
    try {
      const res = await axios.get(`${API_URL}/ai/models`);
      setModelOptions(res.data.models || []);
      if (res.data.default) setModel(res.data.default);
    } catch (err) {
      console.warn('Failed to fetch AI models', err);
    }
  };

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API_URL}/ai/sessions`);
      setSessions(res.data);
    } catch { /* silent */ }
  };

  const fetchMessages = async (sessionId) => {
    try {
      const res = await axios.get(`${API_URL}/ai/sessions/${sessionId}/messages`);
      setMessages(res.data);
    } catch { setMessages([]); }
  };

  const createSession = async () => {
    try {
      const res = await axios.post(`${API_URL}/ai/sessions`, { model });
      setSessions(prev => [res.data, ...prev]);
      setActiveSession(res.data.id);
      setMessages([]);
      setPinnedModel(null);
    } catch { toast.error('Failed to create session'); }
  };

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API_URL}/ai/sessions/${sessionId}`);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (activeSession === sessionId) { setActiveSession(null); setMessages([]); }
    } catch { toast.error('Failed to delete session'); }
  };

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;

    if (!activeSession) {
      try {
        const res = await axios.post(`${API_URL}/ai/sessions`, { model });
        setSessions(prev => [res.data, ...prev]);
        setActiveSession(res.data.id);
        await sendToAPI(res.data.id, msg);
      } catch { toast.error('Failed to start chat'); }
      return;
    }
    await sendToAPI(activeSession, msg);
  };

  const sendToAPI = async (sessionId, msg) => {
    setInput('');
    const userMsg = { id: 'temp-' + Date.now(), role: 'user', content: msg, model };
    const streamId = 'stream-' + Date.now();
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);
    let gotFirstToken = false;

    try {
      const res = await fetch(`${API_URL}/ai/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: axios.defaults.headers.common['Authorization'],
        },
        body: JSON.stringify({ session_id: sessionId, message: msg, model }),
      });
      if (!res.ok) {
        let detail = res.statusText;
        try { detail = (await res.json()).detail || detail; } catch { /* non-json */ }
        throw new Error(detail);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let content = '';
      let resolvedModel = model;

      const handleEvent = (evt) => {
        if (evt.type === 'start') {
          resolvedModel = evt.model_key || model;
          if (resolvedModel !== model) setModel(resolvedModel);
        } else if (evt.type === 'delta') {
          content += evt.content;
          if (!gotFirstToken) {
            gotFirstToken = true;
            setMessages(prev => [...prev, { id: streamId, role: 'assistant', content, model: resolvedModel, streaming: true }]);
          } else {
            setMessages(prev => prev.map(m => m.id === streamId ? { ...m, content } : m));
          }
        } else if (evt.type === 'done') {
          setMessages(prev => prev.map(m => m.id === streamId ? { ...m, id: evt.message_id, streaming: false } : m));
          setPinnedModel(resolvedModel);
        } else if (evt.type === 'error') {
          throw new Error(evt.detail);
        }
      };

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.split('\n').find(l => l.startsWith('data: '));
          if (line) handleEvent(JSON.parse(line.slice(6)));
        }
      }
      fetchSessions(); // refresh titles + pinned model
    } catch (error) {
      toast.error('AI error: ' + error.message);
      setMessages(prev => prev.filter(m => m.id !== userMsg.id && m.id !== streamId));
    } finally {
      setLoading(false);
    }
  };

  // (legacy formatMarkdown removed — now handled by <MarkdownMessage />)

  return (
    <div className="flex h-[calc(100vh-64px)] overflow-hidden" data-testid="ai-assistant-page">
      {/* Sidebar - Sessions */}
      {showSidebar && (
        <div className="w-72 border-r border-slate-200 bg-slate-50 flex flex-col shrink-0">
          <div className="p-3 border-b border-slate-200">
            <Button onClick={createSession} className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-semibold" data-testid="new-chat-btn">
              <Plus className="w-4 h-4 mr-2" />New Chat
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {sessions.length === 0 && (
              <p className="text-xs text-slate-400 text-center mt-8 px-4">No conversations yet. Start a new chat!</p>
            )}
            {sessions.map(s => (
              <div
                key={s.id}
                onClick={() => setActiveSession(s.id)}
                className={`group flex items-center gap-2 p-2.5 rounded-lg cursor-pointer text-sm transition-colors ${
                  activeSession === s.id ? 'bg-orange-100 text-orange-800 font-medium' : 'hover:bg-slate-100 text-slate-600'
                }`}
                data-testid={`session-${s.id}`}
              >
                <MessageSquare className="w-4 h-4 shrink-0 opacity-50" />
                <span className="truncate flex-1">{s.title}</span>
                <button
                  onClick={(e) => deleteSession(s.id, e)}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded transition-opacity"
                  data-testid={`delete-session-${s.id}`}
                >
                  <Trash2 className="w-3 h-3 text-red-400" />
                </button>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-slate-200">
            <p className="text-xs text-slate-400 text-center">A&W AI Assistant</p>
          </div>
        </div>
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Chat Header */}
        <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setShowSidebar(!showSidebar)} className="p-1.5 hover:bg-slate-100 rounded-lg lg:hidden">
              <ChevronLeft className={`w-5 h-5 transition-transform ${!showSidebar ? 'rotate-180' : ''}`} />
            </button>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-50 to-amber-100 flex items-center justify-center overflow-hidden ring-2 ring-orange-200">
              <img src={ROOTY_IMG} alt="Rooty" className="w-9 h-9 object-contain" />
            </div>
            <div>
              <h2 className="font-bold text-slate-900 text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>Rooty <span className="text-orange-500">• A&W AI</span></h2>
              <p className="text-xs text-slate-400">Ask about your stores, sales, audits, and operations</p>
            </div>
          </div>

          {/* Model picker — friendly labels only, never reveals underlying LLM. Locked once a conversation has started. */}
          {modelOptions.length > 0 && (
            <div className="flex items-center gap-2">
              {pinnedModel && (
                <span className="hidden sm:flex items-center gap-1 text-[11px] text-slate-400" data-testid="rooty-model-pinned-hint" title="This conversation stays on the tier it started with. Start a new chat to switch.">
                  <Lock className="w-3 h-3" /> Locked for this chat
                </span>
              )}
              <Select value={model} onValueChange={setModel} disabled={!!pinnedModel}>
                <SelectTrigger
                  data-testid="rooty-model-picker"
                  className="w-[180px] h-9 text-xs rounded-lg border-slate-200 disabled:opacity-80"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {modelOptions.map(m => (
                    <SelectItem key={m.key} value={m.key} data-testid={`rooty-model-${m.key}`}>
                      <div className="flex flex-col">
                        <span className="text-xs font-semibold">{m.label}</span>
                        {m.description && (
                          <span className="text-[10px] text-slate-500">{m.description}</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-4 py-6">
          {messages.length === 0 && !loading ? (
            <div className="max-w-2xl mx-auto text-center mt-12" data-testid="ai-empty-state">
              <div className="w-28 h-28 rounded-full bg-gradient-to-br from-orange-50 to-amber-100 flex items-center justify-center mx-auto mb-5 overflow-hidden ring-4 ring-orange-200/50">
                <img src={ROOTY_IMG} alt="Rooty" className="w-24 h-24 object-contain" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Hi, I'm Rooty 👋
              </h3>
              <p className="text-sm text-slate-500 mb-8">
                Your A&W AI assistant. I can see your store data, KPIs, audits, and financial reports. Ask me anything.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg mx-auto">
                {QUICK_PROMPTS.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(prompt)}
                    className="text-left p-3 bg-white border border-slate-200 rounded-xl hover:border-orange-300 hover:bg-orange-50/50 transition-colors group"
                    data-testid={`quick-prompt-${i}`}
                  >
                    <p className="text-sm text-slate-600 group-hover:text-slate-800 leading-snug">{prompt}</p>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-6">
              {messages.map((msg) => (
                <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'assistant' && (
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 overflow-hidden ring-2 ${
                      msg.model === 'balanced' || msg.model === 'claude' ? 'bg-violet-50 ring-violet-200'
                      : msg.model === 'deep' ? 'bg-indigo-50 ring-indigo-200'
                      : msg.model === 'turbo' ? 'bg-emerald-50 ring-emerald-200'
                      : msg.model === 'pro' ? 'bg-sky-50 ring-sky-200'
                      : msg.model === 'lite' ? 'bg-teal-50 ring-teal-200'
                      : 'bg-orange-50 ring-orange-200'
                    }`}>
                      <img src={ROOTY_IMG} alt="Rooty" className="w-7 h-7 object-contain" />
                    </div>
                  )}
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-orange-500 text-white'
                      : 'bg-white border border-slate-200 text-slate-700'
                  }`}>
                    {msg.role === 'user' ? (
                      <p className="text-sm leading-relaxed">{msg.content}</p>
                    ) : (
                      <div className={msg.streaming ? 'rooty-streaming' : ''} data-testid={msg.streaming ? 'ai-streaming-message' : undefined}>
                        <MarkdownMessage className="prose-sm">{msg.content}</MarkdownMessage>
                      </div>
                    )}
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-7 h-7 rounded-lg bg-slate-200 flex items-center justify-center shrink-0 mt-0.5">
                      <User className="w-4 h-4 text-slate-500" />
                    </div>
                  )}
                </div>
              ))}
              {loading && !messages.some(m => m.streaming) && (
                <div className="flex gap-3 justify-start">
                  <div className="w-8 h-8 rounded-full bg-orange-50 ring-2 ring-orange-200 flex items-center justify-center shrink-0 overflow-hidden">
                    <img src={ROOTY_IMG} alt="Rooty" className="w-7 h-7 object-contain" />
                  </div>
                  <div className="bg-white border border-slate-200 rounded-2xl px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                      <span className="text-sm text-slate-400">
                        Rooty is thinking...
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="shrink-0 border-t border-slate-200 bg-white px-4 py-3">
          <div className="max-w-3xl mx-auto flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }}}
              placeholder="Ask Rooty about your business..."
              className="flex-1 rounded-xl border-slate-200 focus:border-orange-300 text-sm"
              disabled={loading}
              data-testid="ai-chat-input"
            />
            <Button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl px-4"
              data-testid="ai-send-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
